import { UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Modal, Table, Tag } from 'antd';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { claimReward, historyPredictions } from 'service/base';
import 'styles/global.css';

const HistoryPredictions = ({
  isModalVote,
  setIsModalVote,
  IP,
  FP,
  variant = 'modal'
}) => {
  const isPage = variant === 'page';
  const { notification } = App.useApp();
  const [formHistory] = Form.useForm();
  const [account, setAccount] = useState('');
  const [dataPrediction, setDataPrediction] = useState([]);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const fetchHistoryPredictions = async (accountValue) => {
    const normalizedAccount = (accountValue ?? '').trim();
    if (!normalizedAccount) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đang nhập!'
      });
      return;
    }

    setIsLoadingSubmit(true);
    try {
      const res = await historyPredictions({
        account: normalizedAccount,
        site: SITE,
        ip: IP,
        fp: FP
      });

      setDataPrediction(res?.data || []);
      if (!res?.data?.responseData?.[0]) {
        notification.warning({
          message: 'Không có lịch sử',
          description: 'Không tìm thấy lịch sử bình chọn, Quý khách vui lòng kiểm tra lại!'
        });
      }
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const defaultPagination = {
    current: 1,
    pageSize: 15
  };
  const [pagination, setPagination] = useState({
    current: defaultPagination.current,
    pageSize: defaultPagination.pageSize,
    total: dataPrediction?.totalRecords || 0,
    size: 'default',
    onChange: (page, pageSize) => {
      console.log('Page change:', page, pageSize);
      setPagination((prev) => ({ ...prev, current: page, pageSize }));
    }
  });

  // Load saved account: full page on mount; modal when opened
  useEffect(() => {
    if (isPage) {
      const savedAccount = localStorage.getItem('userAccount');
      if (savedAccount) {
        setAccount(savedAccount);
        formHistory.setFieldsValue({ account: savedAccount });
      }
      return;
    }
    if (isModalVote) {
      const savedAccount = localStorage.getItem('userAccount');
      if (savedAccount) {
        setAccount(savedAccount);
        formHistory.setFieldsValue({ account: savedAccount });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPage, isModalVote]);

  const handleCancelVote = () => {
    if (!isPage && setIsModalVote) setIsModalVote(false);
  };
  const onFinishHistoryForm = async (data) => {
    console.log('History form submitted with data:', data);
    const normalizedAccount = (data?.account ?? '').trim();
    console.log('Using account:', normalizedAccount);
    setAccount(normalizedAccount);
    await fetchHistoryPredictions(normalizedAccount);
  };

  const handleClaimReward = async (item) => {
    console.log('Claiming reward for item:', item);
    if (item.status_register !== 'apply') {
      return;
    }

    try {
      const accountValue = (formHistory.getFieldValue('account') ?? account).trim().toLowerCase();
      const response = await claimReward({
        account: accountValue,
        site: SITE,
        matchId: item.matchId
      });

      if (response?.code === 0) {
        notification.success({ message: 'Thành công', description: 'Nhận thưởng thành công!' });
        // Refresh the history
        await fetchHistoryPredictions(account);
      }
    } catch (error) {
      console.error('Error claiming reward:', error);
      notification.error({
        message: 'Nhận thưởng thất bại!',
        description: error.message
      });
    }
  };

  const onFinishHistoryFailed = () => {
    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng kiểm tra lại tài khoản !'
    });
  };

  const columns = [
    {
      title: 'Trận đấu',
      render: (_, record) => {
        return record.homeName + ' - ' + record.awayName;
      }
    },
    {
      title: 'Bình chọn',
      width: '15%',
      render: (_, record) => {
        let tagColor = '#000000'; // Default: black
        if (record.status_register === 'deny') {
          tagColor = 'red';
        } else if (record.status_register === 'apply') {
          tagColor = 'green';
        }
        return (
          <Tag className="text-center" color={tagColor} key={record._id}>
            {record.homeScore}-{record.awayScore}
          </Tag>
        );
      }
    },
    {
      title: 'Kết quả',
      width: '15%',
      render: (text, record) => {
        if (record.match_result) {
          return (
            <Tag className="text-center" color="#00890a" key={record._id}>
              {record.match_result}
            </Tag>
          );
        } else {
          return null;
        }
      }
    },
    {
      title: 'Ngày dự đoán',
      dataIndex: 'createdAt',
      render: (_, record) => dayjs(_).format('DD/MM/YYYY')
    },
    {
      title: 'Nhận thưởng',
      width: '20%',
      render: (_, item) => (
        <div className="flex justify-start">
          <button
            onClick={() => handleClaimReward(item)}
            disabled={item.status_register !== 'apply'}
            className={`px-1 md:px-3 py-2 text-xs md:text-sm rounded-md md:rounded-lg text-white border-none font-semibold ${item.status_register === 'apply' ? 'bg-[#F49A25] cursor-pointer' : 'bg-[#929292] cursor-not-allowed'}`}
          >
            {item.status_register === 'accept' ? 'Đã nhận thưởng' : 'Nhận thưởng'}
          </button>
        </div>
      )
    }
  ];

  const content = (
    <div className="content-km content-history">
      <Form
        name="formHistoryMatch"
        onFinish={onFinishHistoryForm}
        onFinishFailed={onFinishHistoryFailed}
        className="relative"
        autoComplete="off"
        form={formHistory}
        initialValues={{ account: account }}
      >
        <div className="flex justify-center items-center gap-2 md:py-4 py-1 w-full relative">
          <Form.Item
            name="account"
            className="mb-0 min-w-[20%] md:min-h-[38px] w-full max-w-[300px]"
            rules={[
              {
                required: true,
                message: 'Vui lòng điền tên đăng nhập!'
              }
            ]}
          >
            <Input
              className="min-h-[38px] w-full md:min-w-[100px] md:max-w-[300px]"
              placeholder="Nhập tên đăng nhập ..."
              maxLength={30}
              prefix={<UserOutlined />}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoadingSubmit}
            className="bg-score bg-check-account text-white font-medium min-h-[38px]"
          >
            Xác Nhận
          </Button>
        </div>
      </Form>
      <div className="list-history w-full max-w-full min-w-0 overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <Table
          size="small"
          columns={columns}
          dataSource={dataPrediction?.responseData || []}
          loading={isLoadingSubmit}
          pagination={pagination}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );

  if (isPage) {
    return (
      <div>
        <h2 className="text-center text-lg md:text-xl font-bold text-[#f27121] mb-4 px-2">
          DANH SÁCH LỊCH SỬ DỰ ĐOÁN
        </h2>
        {content}
      </div>
    );
  }

  return (
    <Modal
      className="modal-jun88k36 modal-history"
      title="DANH SÁCH LỊCH SỬ DỰ ĐOÁN"
      open={isModalVote}
      footer={null}
      onCancel={handleCancelVote}
    >
      {content}
    </Modal>
  );
};

export default HistoryPredictions;
